import { useState, useEffect, useRef } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { Mic, Play, Square, Pause, Trash2, Type, Lock, X, Upload, FileText, Sparkles, Globe, ShieldCheck, Check, RotateCcw, Headphones, Eye } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../../stores'
import useQuestionnaireStore from '../../stores/useQuestionnaireStore'
import { useI18n } from '../../i18n'
import { isDescribeLocked } from '../../utils/describeLock'
import { micFailureMessage, micFailureKind, micPermissionHelp, MIC_DENIED } from '../../components/voice/transcriptionOutcome'
import { isNativeApp } from '../../utils/authStorage'
import { transcriptionAPI, aiAPI, resolveFileUrl } from '../../services/api'
import { startTranscribeStream } from '../../lib/transcribeStream'
import { prefillFromTranscript, prefillFromProfile, getAllQuestionsForCase } from '../../utils/voicePrefill'
import DeleteCaseModal from '../../components/DeleteCaseModal'
import BottomActionBar from '../../components/BottomActionBar'
import { isHeldByDoctor, reportDeleteFailure } from '../../utils/caseDelete'
import { ChatKeyframes, CompactHeader, OrellaChatBubble } from '../../components/OrellaChatHeader'
// v242.68.0 (FU-VOICE-PARITY-1): the recorder surface is now one shared,
// PRESENTATIONAL module. No state moved out of this file — see the header of
// components/voice/VoiceRecorder.jsx for why that was the constraint.
import {
  VoiceRecorderKeyframes, useMicLevel, RecorderIdleHero, RecorderLivePanel,
  TidyingPanel,
} from '../../components/voice/VoiceRecorder'
import {
  isTooShortToTranscribe, streamUnavailableMessage, nothingRecordedMessage,
} from '../../components/voice/transcriptionOutcome'
import { caseBack } from '../../utils/caseBack' // PROD-654: back = the previous case screen
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// Color constants
const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'

const MAX_RECORDING_TIME = 180 // 3 minutes for patient voice notes

export default function DescribeVoice() {
  const navigate = useNavigate()
  const { currentCase, updateCase, updateAndSaveCase, clearCurrentCase, deleteCase } = useCaseStore()
  const { getQuestions } = useQuestionnaireStore()
  const profile = useProfileStore((s) => s.profile)
  const { t, language } = useI18n()
  
  // v242.235.0: answers lock once the summary is written (utils/describeLock.js owns the rule).
  const isLocked = isDescribeLocked(currentCase)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  
  const [showContent, setShowContent] = useState(() => {
    return !!sessionStorage.getItem(`orella_chat_voice_${currentCase?.id}`)
  })
  
  // ═══════════════════════════════════════════════════════════════════════
  // VOICE STATE
  // ═══════════════════════════════════════════════════════════════════════
  // voiceState: 'idle' | 'recording' | 'paused' | 'transcribing' | 'complete'
  const [voiceState, setVoiceState] = useState('idle')
  const [recordingTime, setRecordingTime] = useState(0)
  const [transcript, setTranscript] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackTime, setPlaybackTime] = useState(0)
  const [error, setError] = useState(null)
  const [permissionDenied, setPermissionDenied] = useState(false)
  // Live transcript is captured but NOT rendered (design decision: raw
  // filler-heavy text is never shown to the patient; they review the tidied
  // version instead). Kept in state only to drive the "hearing you" hint.
  const [heardSpeech, setHeardSpeech] = useState(false)
  // v242.57.0 (D-TIDY): the streamed transcript is kept verbatim alongside the
  // tidied version the patient reviews. `wasTidied` gates the disclosure pill
  // and the "see what I actually said" affordance — it goes false the moment
  // the patient edits or reverts, because after that the claim is untrue.
  const [rawTranscript, setRawTranscript] = useState('')
  const [wasTidied, setWasTidied] = useState(false)
  const [showOriginal, setShowOriginal] = useState(false)
  const { level, attach: attachMicLevel, detach: detachMicLevel } = useMicLevel('DescribeVoice')
  const [confidence, setConfidence] = useState(null)
  const [detectedLang, setDetectedLang] = useState(null)
  // v223.9.4 (hotfix): Next commits to the DB (awaited updateAndSaveCase) before
  // navigating, which leaves a 0.5–2s gap with no feedback. Drive a spinner +
  // disabled state off this flag so the patient knows the tap registered.
  const [isAdvancing, setIsAdvancing] = useState(false)
  
  // Text input state
  const [textInput, setTextInput] = useState('')
  // v207.1.0 (Smart merge): `inputMode` retired. A single unified flow now handles both voice
  // and keyboard input. The transcript card appears once ANY content exists (recording or
  // typed text). `hasTypedStart` flips true when the user taps "type instead" on the idle
  // hero, revealing the editable card without needing a recording first.
  const [hasTypedStart, setHasTypedStart] = useState(false)
  
  // Refs
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const audioUrlRef = useRef(null)
  const audioBlobRef = useRef(null)
  const audioElementRef = useRef(null)
  const timerRef = useRef(null)
  const recordingTimeRef = useRef(0)
  const initialLoadDoneRef = useRef(false)
  const textareaRef = useRef(null)

  // v242.53.0 (AI-2): live Transcribe streaming. The stream is PRIMARY; the
  // MediaRecorder blob remains as insurance and is what gets saved to S3
  // only if streaming produced nothing.
  const streamSessionRef = useRef(null)
  const streamCommittedRef = useRef('')
  const streamInfoRef = useRef(null)
  // v242.55.0: real amplitude for the level meter, on the same mic stream the
  // recorder uses. v242.68.0: that machinery moved into useMicLevel() so the
  // follow-up recorder gets the identical meter instead of a second one.

  // v213.3 (C-A-186): Auto-resize textarea to fit content whenever value changes
  // (covers initial mount, voice-transcript load, etc.). Internal scroll is
  // disabled in the textarea className, so this is what gives the field room
  // to grow without trapping page scroll. Page scroll on the outer wrapper
  // handles transcripts taller than the viewport.
  //
  // v217.1.1: Two fixes for the "transcript clipped after recording" bug:
  //   1. Add voiceState to deps — the textarea isn't mounted while
  //      voiceState === 'transcribing', so the effect's earlier fire (when
  //      transcript was just set) hit a null ref. When voiceState then flips
  //      to 'complete' the textarea mounts, but without voiceState in deps
  //      the effect never re-runs and the height stays at min-h-[130px].
  //   2. Remove the 600px cap — it silently clipped long transcripts
  //      because the textarea is `overflow-hidden` for the touch-scroll-trap
  //      fix. Letting the textarea grow to full content height keeps that
  //      fix intact (still no inner scroll) and the outer `overflow-auto`
  //      wrapper handles page-level scroll for any height.
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = ta.scrollHeight + 'px'
  }, [textInput, transcript, voiceState])

  // ═══════════════════════════════════════════════════════════════════════
  // LIFECYCLE
  // ═══════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    window.scrollTo(0, 0)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current)
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
    }
  }, [])

  // Load existing data on mount
  useEffect(() => {
    if (initialLoadDoneRef.current) return
    
    if (currentCase?.voiceRecording?.audioData) {
      audioUrlRef.current = currentCase.voiceRecording.audioData
      setRecordingTime(currentCase.voiceRecording.durationSeconds || 0)
      setConfidence(currentCase.voiceRecording.transcriptionConfidence || 96)
      setDetectedLang(currentCase.voiceRecording.detectedLanguage || 'English')
      
      const existingTranscript = currentCase.voiceRecording.transcript || currentCase.voiceTranscript
      if (existingTranscript) {
        setTranscript(existingTranscript)
        setTextInput(existingTranscript)
      }
      setVoiceState('complete')
    }

    // v242.57.0 — the streamed path never writes `audioData` (audio goes to
    // AWS directly and is never base64'd into the case), so the block above
    // misses it entirely and a streamed recording came back on reload with no
    // 'complete' state and no meta pills. Recognise it by its own marker.
    const vr = currentCase?.voiceRecording
    if (vr && !vr.audioData && vr.transcriptionSource === 'transcribe_stream') {
      setRecordingTime(vr.durationSeconds || 0)
      setDetectedLang(vr.detectedLanguage || null)
      const existing = vr.transcript || currentCase?.voiceTranscript
      if (existing) { setTranscript(existing); setTextInput(existing) }
      setVoiceState('complete')
    }

    // v242.58.0 (B64-2) — audio now lives in storage, so playback on revisit
    // needs a presigned URL. Legacy rows still carry base64 in `audioData` and
    // are handled by the block above. resolveFileUrl passes data:/http through
    // untouched, so this is safe for both shapes.
    if (vr?.storagePath && !vr.audioData) {
      setRecordingTime(vr.durationSeconds || 0)
      resolveFileUrl(vr.storagePath)
        .then(url => { if (url) audioUrlRef.current = url })
        .catch(() => { /* transcript still stands; only playback is lost */ })
      setVoiceState('complete')
    }

    // Restore the tidy disclosure. `wasTidied` is only true while the stored
    // transcript still equals the tidied string — if the patient edited it in
    // a previous session, the claim is stale and must not come back.
    if (vr?.rawTranscript) {
      setRawTranscript(vr.rawTranscript)
      const shown = vr.transcript || currentCase?.voiceTranscript || ''
      setWasTidied(
        vr.wasTidied === true &&
        !!vr.tidiedTranscript &&
        shown.trim() === vr.tidiedTranscript.trim()
      )
    }

    if (currentCase?.voiceTextInput) {
      setTextInput(currentCase.voiceTextInput)
      // v207.1.0: No audio but has typed text → reveal the editable card on mount
      if (!currentCase?.voiceRecording?.audioData) setHasTypedStart(true)
    }
    
    if (currentCase?.voiceTranscript && !transcript) {
      setTranscript(currentCase.voiceTranscript)
      setTextInput(currentCase.voiceTranscript)
    }

    // v213.10: If the case has no voice/text yet but the user typed a chief
    // complaint in the home chat box recently, seed it here as the voice memo
    // equivalent. This makes the home-typed text behave identically to a voice
    // recording or DescribeVoice-typed text — it flows into voiceTextInput and
    // voiceTranscript, which are what every downstream AI function reads.
    // Applies to ALL specialty paths, not just general.
    const hasAnyVoiceContent = !!(
      currentCase?.voiceRecording?.audioData ||
      currentCase?.voiceTextInput ||
      currentCase?.voiceTranscript
    )
    if (!hasAnyVoiceContent) {
      try {
        const seed = sessionStorage.getItem('orella_home_triage_input')
        const seedTs = parseInt(sessionStorage.getItem('orella_home_triage_ts') || '0', 10)
        // 30 minute freshness window — if the user dawdled longer, ignore stale text
        if (seed && seedTs && (Date.now() - seedTs) < 30 * 60 * 1000) {
          console.log('[DescribeVoice] Seeding from home triage, length:', seed.length)
          setTextInput(seed)
          setTranscript(seed)
          setHasTypedStart(true)
          // Persist to the case so the API receives it (the augment endpoint and
          // every other AI prompt reads voice_text_input from the DB).
          updateAndSaveCase({
            voiceTextInput: seed,
            voiceTranscript: seed,
          })
          // Clear the seed so a subsequent unrelated case doesn't pick it up
          sessionStorage.removeItem('orella_home_triage_input')
          sessionStorage.removeItem('orella_home_triage_ts')
        }
      } catch (e) {
        console.warn('[DescribeVoice] Home-triage seed failed (non-fatal):', e?.message)
      }
    }

    initialLoadDoneRef.current = true
  }, [currentCase])

  // DELETE-CURRENTCASE-NULL-1 — DECLARATIVE REDIRECT, NOT navigate() IN RENDER.
  // Calling navigate() here updates the router WHILE this component is rendering, and
  // React logs exactly that: "Cannot update a component (BrowserRouter) while rendering
  // a different component". That is the React error a patient saw when they deleted a
  // case mid-describe — deleteCase() nulls currentCase, this screen re-rendered on the
  // null and took this arm. The delete and the redirect both completed, which is why it
  // read as cosmetic. <Navigate> is the supported way to say the same thing.
  if (!currentCase) return <Navigate to="/home" replace />

  // ═══════════════════════════════════════════════════════════════════════
  // RECORDING HANDLERS
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleStartRecording = async () => {
    setError(null)
    audioChunksRef.current = []
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 } 
      })
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data)
      }
      
      mediaRecorder.onstop = async () => {
        // Close the stream FIRST so the trailing partial flushes (v242.53.0).
        //
        // The `await new Promise(r => setTimeout(r, 900))` that used to follow
        // this line is gone: stop() now drains the flush itself and does not
        // resolve until Transcribe has closed the socket or FLUSH_TIMEOUT_MS
        // has passed (lib/transcribeStream.js). This screen was the ONE call
        // site of five that remembered to wait, which is exactly why Describe
        // transcribed and the follow-up report did not — FU-STREAM-DRAIN-1.
        // Do not put the sleep back; a second drain is a second answer.
        const session = await streamSessionRef.current
        if (session) {
          try { await session.stop() } catch (e) { /* noop */ }
        }
        streamSessionRef.current = null
        detachMicLevel()
        stream.getTracks().forEach(track => track.stop())
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType })
        audioBlobRef.current = audioBlob
        
        if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current)
        audioUrlRef.current = URL.createObjectURL(audioBlob)
        
        const finalDuration = recordingTimeRef.current

        // v242.53.0 (AI-2): streaming is PRIMARY. If it produced text, use it
        // No PHI audio leaves the BAA.
        const streamed = streamCommittedRef.current.trim()
        if (streamed) {
          await completeFromStream(streamed, finalDuration, mimeType)
          return
        }

        // TF-23 — the report is "click the mic on and off before anything
        // records". There is no speech in a sub-second capture, so asking the
        // server produces its configuration-fallback envelope and the patient
        // reads a deployment instruction. Nothing failed here; nothing was
        // said. Answer that directly and send no audio anywhere.
        if (isTooShortToTranscribe(audioBlob, finalDuration)) {
          console.warn('[DescribeVoice] capture too short to transcribe:', audioBlob.size, 'bytes /', finalDuration, 's')
          if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null }
          audioChunksRef.current = []
          audioBlobRef.current = null
          setError(nothingRecordedMessage(t, 'describeVoice'))
          setRecordingTime(0)
          recordingTimeRef.current = 0
          setVoiceState('idle')
          return
        }

        // Streaming yielded nothing and there is no second engine to appeal
        // to (WHISPER-REMOVAL-1). The recording is saved above; say what
        // happened and leave the patient the textarea.
        setError(streamUnavailableMessage(null, t, 'describeVoice'))
        setVoiceState('complete')
      }
      
      mediaRecorder.onerror = () => {
        setError(t('errors2.recordingFailed'))
        setVoiceState('idle')
      }
      
      mediaRecorder.start(1000)
      setVoiceState('recording')
      setRecordingTime(0)
      recordingTimeRef.current = 0
      setTranscript('')
      setIsEditing(false)

      // v242.55.0: level meter driven by real amplitude. Fail-soft inside.
      attachMicLevel(stream)

      // v242.53.0 (AI-2): open the Transcribe stream on the SAME mic stream.
      // Non-blocking and fail-soft — if this throws, recording continues and
      // the patient is told and types instead.
      streamCommittedRef.current = ''
      streamInfoRef.current = null
      setHeardSpeech(false)
      // ★ THE PROMISE GOES IN THE REF, NOT THE RESOLVED SESSION (FU-STREAM-MINT-1).
      // Not awaited on purpose — recording starts on the tap, not after the
      // presigned URL round trip — but a recording shorter than the mint used to
      // reach onstop with a null ref and never drain. See FollowUpReportModal.
      streamSessionRef.current = startTranscribeStream({
        stream,
        mint: () => transcriptionAPI.streamUrl({
          surface: 'patient',
          language: (currentCase?.patientLanguage || language || 'en').slice(0, 2),
        }),
        onReady: (info) => {
          streamInfoRef.current = info
          console.log('[DescribeVoice] stream ready', info)
        },
        onPartial: () => setHeardSpeech(true),
        onCommit: (text) => {
          streamCommittedRef.current = (streamCommittedRef.current + ' ' + text).trim()
          setHeardSpeech(true)
        },
        onError: (err) => console.warn('[DescribeVoice] stream error:', err.name, err.message),
        onClose: (i) => console.log('[DescribeVoice] stream closed', i),
      }).catch((err) => {
        console.warn('[DescribeVoice] streaming unavailable; the patient types instead:', err.message)
        return null
      })
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1
          recordingTimeRef.current = newTime
          if (newTime >= MAX_RECORDING_TIME) { handleStopRecording(); return prev }
          return newTime
        })
      }, 1000)
      
    } catch (err) {
      // v242.172.0 APL-3 — one owner for "why did the microphone not open". This screen
      //   had the most complete chain of the three and it was still a copy; the follow-up
      //   report's copy handled no-microphone not at all.
      if (micFailureKind(err) === MIC_DENIED) setPermissionDenied(true)
      setError(micFailureMessage(err, t, 'describeVoice', isNativeApp()))
    }
  }

  const handleStopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
  }

  const handlePauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause()
      clearInterval(timerRef.current)
      setVoiceState('paused')
    }
  }

  const handleResumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume()
      setVoiceState('recording')
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1
          recordingTimeRef.current = newTime
          if (newTime >= MAX_RECORDING_TIME) { handleStopRecording(); return prev }
          return newTime
        })
      }, 1000)
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // STREAMED TRANSCRIPT COMPLETION (v242.53.0 — AI-2 primary path)
  // ═══════════════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════════════
  // VOICE AUDIO PERSISTENCE (v242.58.0 — B64-2)
  // ═══════════════════════════════════════════════════════════════════════
  // Patient audio is stored by REFERENCE: the blob goes to the private PHI
  // bucket via a server-minted presigned PUT, and only the storage PATH is
  // written to the case row. The MD panel presigns it back on read.
  //
  // Fail-soft, and deliberately NOT fail-back-to-base64: if the upload fails
  // the case keeps its transcript and loses only the audio. Writing base64 as
  // a consolation prize is the exact defect this closes.
  const uploadVoiceBlob = async (blob, mimeType) => {
    if (!currentCase?.id || !blob || blob.size === 0) return null
    try {
      const { uploadToStorage } = await import('../../services/storage')
      const ext = String(mimeType).includes('mp4') ? 'mp4' : 'webm'
      const path = await uploadToStorage(
        currentCase.id,
        `voice-note-${Date.now()}.${ext}`,
        blob,
        mimeType
      )
      if (!path) console.warn('[DescribeVoice] voice upload returned no path; case keeps transcript only')
      return path || null
    } catch (err) {
      console.warn('[DescribeVoice] voice upload failed; case keeps transcript only:', err?.message || err)
      return null
    }
  }

  const completeFromStream = async (transcriptText, duration, mimeType) => {
    setVoiceState('tidying')
    // v242.54.0: no 'transcribing' state on this path. Nothing uploads and
    // nothing is transcribed after the fact — the text already arrived live.
    // Showing the Whisper progress sequence here would be theatre for work
    // that did not happen.
    setConfidence(null)
    setDetectedLang(null)

    // v242.57.0 (D-TIDY): raw is the patient's actual words and the only
    // irreplaceable thing here. Persist it BEFORE the tidy round trip, so a
    // hang, a crash, or a backgrounded app mid-tidy cannot lose it. Costs one
    // extra save; buys crash-safety on the one field we cannot regenerate.
    const baseRecording = {
      ...currentCase?.voiceRecording,
      mimeType,
      durationSeconds: duration,
      duration: formatTime(duration),
      recordedAt: new Date().toISOString(),
      rawTranscript: transcriptText,
      transcriptionStatus: 'completed',
      transcriptionSource: 'transcribe_stream',
      detectedLanguage: streamInfoRef.current?.languageCode || null,
    }

    setRawTranscript(transcriptText)
    await updateAndSaveCase({
      hasVoiceNote: true,
      voiceNoteDuration: duration,
      voiceRecording: { ...baseRecording, transcript: transcriptText, wasTidied: false },
      voiceTranscript: transcriptText,
      voiceTextInput: transcriptText,
    })

    // v242.58.0 — the streaming path previously persisted NO audio at all,
    // because it never produced base64 and nothing else wrote a reference. The
    // MD panel reads voice_recording_url to drive its player, so every case
    // recorded since v242.53.0 showed "Transcript only" there. Upload the blob
    // and store its path.
    //
    // Runs CONCURRENTLY with the tidy pass — two independent network calls,
    // neither gates the other, and the patient waits for the slower one rather
    // than the sum.
    const [storagePath, tidyResult] = await Promise.all([
      uploadVoiceBlob(audioBlobRef.current, mimeType),
      // Cosmetic pass. EVERY failure mode falls through to the raw transcript
      // without telling the patient anything — a filler-word cleanup is not
      // worth an error state on this screen.
      transcriptionAPI.tidy(transcriptText).catch(err => {
        console.warn('[DescribeVoice] tidy failed, using raw transcript:', err?.message || err)
        return null
      }),
    ])

    let finalText = transcriptText
    let tidied = false
    const candidate = (tidyResult?.tidied || '').trim()
    if (tidyResult?.wasTidied && candidate) {
      finalText = candidate
      tidied = true
    } else if (tidyResult?.reason) {
      console.warn('[DescribeVoice] tidy declined')
    }

    setTranscript(finalText)
    setTextInput(finalText)
    setWasTidied(tidied)
    setShowOriginal(false)

    // Second save whenever EITHER result needs persisting. Gating this on
    // `tidied` alone would silently drop the storage path on any recording the
    // tidy pass declined.
    if (tidied || storagePath) {
      await updateAndSaveCase({
        hasVoiceNote: true,
        voiceNoteDuration: duration,
        voiceRecording: {
          ...baseRecording,
          ...(storagePath ? { storagePath } : {}),
          transcript: finalText,
          ...(tidied ? { tidiedTranscript: finalText, wasTidied: true } : {}),
        },
        voiceTranscript: finalText,
        voiceTextInput: finalText,
      })
    }

    // Prefill reads the text the patient will actually review and send.
    runPrefillFromText(finalText)
    setVoiceState('complete')
  }

  // v242.57.0 — the patient chose their original wording over the tidied
  // version. This SWAPS what the textarea shows; it never destroys either
  // string. Both stay on the case row.
  const handleUseOriginal = () => {
    if (isLocked || !rawTranscript) return
    setTranscript(rawTranscript)
    setTextInput(rawTranscript)
    setWasTidied(false)
    setShowOriginal(false)
    updateAndSaveCase({ voiceTranscript: rawTranscript, voiceTextInput: rawTranscript })
  }

  // ═══════════════════════════════════════════════════════════════════════
  // TRANSCRIPTION — the legacy batch leg was removed at WHISPER-REMOVAL-1
  // ═══════════════════════════════════════════════════════════════════════
  
  // startTranscription — REMOVED (WHISPER-REMOVAL-1). It posted the
  // recording as base64 to the batch endpoint and rendered whatever came
  // back. OPENAI_API_KEY has been absent from every secret group since
  // D48b, so what came back was always the configuration envelope and the
  // patient always saw an error. Amazon Transcribe streaming is the only
  // engine now; when it does not connect, the patient types.

  // ═══════════════════════════════════════════════════════════════════════
  // PLAYBACK & DELETE
  // ═══════════════════════════════════════════════════════════════════════
  
  const handlePlayPause = () => {
    if (!audioElementRef.current) {
      audioElementRef.current = new Audio(audioUrlRef.current)
      audioElementRef.current.onended = () => { setIsPlaying(false); setPlaybackTime(0) }
      audioElementRef.current.ontimeupdate = () => setPlaybackTime(Math.floor(audioElementRef.current.currentTime))
    }
    if (isPlaying) { audioElementRef.current.pause(); setIsPlaying(false) }
    else { audioElementRef.current.play(); setIsPlaying(true) }
  }

  const handleDeleteRecording = () => {
    if (audioElementRef.current) { audioElementRef.current.pause(); audioElementRef.current.src = ''; audioElementRef.current = null }
    if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null }
    audioChunksRef.current = []
    audioBlobRef.current = null
    if (mediaRecorderRef.current?.state !== 'inactive') {
      try { mediaRecorderRef.current?.stop() } catch (e) {}
    }
    mediaRecorderRef.current = null
    
    setVoiceState('idle')
    setRecordingTime(0); recordingTimeRef.current = 0
    setPlaybackTime(0); setIsPlaying(false)
    setTranscript(''); setIsEditing(false); setTextInput('')
    setConfidence(null); setDetectedLang(null)
    setError(null)
    setHasTypedStart(false) // v207.1.0: return to idle hero, not the typed card
    // v242.57.0: a stale pill or a previous recording's raw text must never
    // survive into the next recording.
    setRawTranscript(''); setWasTidied(false); setShowOriginal(false)
    
    updateAndSaveCase({
      hasVoiceNote: false, voiceNoteDuration: 0,
      voiceRecording: null, voiceTranscript: null, voiceTextInput: null
    })
  }

  // ═══════════════════════════════════════════════════════════════════════
  // NAVIGATION
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleDeleteCase = async () => {
    // DELETE-CURRENTCASE-NULL-1 — NAVIGATE FIRST, DELETE SECOND.
    // deleteCase() sets currentCase to null in the store. Awaiting it here re-rendered
    // THIS screen against that null while it was still mounted, and the first thing in
    // render that reads currentCase.<x> without a ?. threw a React error. The delete and
    // the navigation both completed, which is exactly why it looked cosmetic: the app
    // "worked" and threw anyway. Unmounting first removes the whole class, at every one
    // of the six screens that can delete their own case, rather than optional-chaining
    // whichever line happened to fire (RULES §1bv(a)).
    const id = currentCase?.id
    setShowDeleteModal(false)
    navigate('/home', { replace: true })
    // ★ PROD-416: a delete the server refuses leaves the case, and the patient is told.
    if (id) { try { await deleteCase(id) } catch (err) { reportDeleteFailure(t, err) } }
    else clearCurrentCase()
  }
  
  const handleSaveAsDraft = async () => {
    if (currentCase?.id) {
      await updateAndSaveCase({ status: 'draft', voiceTextInput: textInput || currentCase.voiceTextInput })
    }
    setShowDeleteModal(false)
    navigate('/cases', { replace: true })
  }

  // v206.9.1: Shared prefill runner — works on any text source (Whisper transcript,
  // typed textarea input, or edited transcript). Called from both the Whisper success
  // callback AND handleNext so typed input no longer bypasses survey auto-population.
  // Idempotent: safe to run twice (answers spread/overwrite cleanly).
  //
  // v219.5.0: PRIMARY path is now the Sonnet-backed /ai/extract-survey-answers
  // endpoint. The old client-side regex matcher (prefillFromTranscript) had no
  // concept of negation / speaker attribution / paraphrase / mutual exclusion
  // and produced confidently-wrong answers (e.g. "no dizziness" → "Yes - vertigo").
  // The AI extractor returns only HIGH-confidence answers, in the same answer-map
  // shape. If the AI call fails for ANY reason (offline, 5xx, timeout, AI service
  // down) we silently fall back to the legacy regex prefill so the intake flow is
  // never blocked. Profile-based prefill is always applied (cheap, deterministic).
  //
  // Still fire-and-forget for callers: they don't await this. Existing
  // user-entered answers are never clobbered; prefills only fill blanks.
  // v220.11: prefillKeys is a sibling map flagging which keys in `answers`
  // came from a prefill source (vs. patient-clicked). Carrying this metadata
  // lets us:
  //   (a) drop STALE prefill entries from a prior run before merging fresh
  //       ones — without this, persisted localStorage answers from earlier
  //       tests poisoned the next run (root cause of "two prior fixes still
  //       broken"). User-entered answers are NOT in prefillKeys, so they're
  //       preserved untouched.
  //   (b) Honor "Sonnet high-confidence wins" cleanly. When AI returns
  //       results we trust the AI map for those keys.
  // Click handlers in DescribeSection remove a key from prefillKeys the
  // moment the patient touches it — promoting it to user-entered.
  const applyPrefills = (combinedPrefills, sourceLabel, freshKeyTags = {}) => {
    const totalCount = Object.keys(combinedPrefills).length
    if (totalCount > 0) {
      const existingAnswers = currentCase?.answers || {}
      const existingPrefillKeys = currentCase?.prefillKeys || {}

      // Strip STALE prefill entries: any answer key whose value was itself
      // prefilled (not patient-entered) is discarded before merging. This is
      // the architecture-level fix (Option A): tagged prefills are
      // replaceable; user-entered answers are not.
      const userEnteredAnswers = {}
      for (const [k, v] of Object.entries(existingAnswers)) {
        if (!existingPrefillKeys[k]) {
          userEnteredAnswers[k] = v
        }
      }

      // Merge order: fresh prefills first, then user-entered answers ON TOP.
      // User-entered always wins. Fresh prefills replace any stale prefill
      // (because stale prefills were stripped above).
      const merged = { ...combinedPrefills, ...userEnteredAnswers }

      // prefillKeys: drop stale tags, add fresh tags. User-entered keys are
      // never added here, so handleSingleSelect/handleToggleOption don't
      // need to deal with them.
      const newPrefillKeys = { ...freshKeyTags }
      // Keep stale tags only for keys that are STILL prefilled in the new
      // merged answers (i.e. the user hasn't entered them and the fresh
      // prefill didn't overwrite them). In practice this means: drop ALL
      // stale tags whose keys are not in `combinedPrefills`, because those
      // values were stripped above. The result is exactly freshKeyTags.
      // (Left explicit for future readers.)

      updateCase({
        answers: merged,
        prefillKeys: newPrefillKeys,
        voicePrefilled: true,
        voicePrefillCount: Object.keys(combinedPrefills).length,
      })
      const stripped = Object.keys(existingPrefillKeys).filter(k => !combinedPrefills[k]).length
      console.log(`[DescribeVoice] ✅ Pre-filled ${totalCount} answer(s) (${sourceLabel}); stripped ${stripped} stale prefill(s); preserved ${Object.keys(userEnteredAnswers).length} user-entered`)
    }
    return totalCount
  }

  const runPrefillFromText = async (sourceText) => {
    // v219.5.7: surveyExtractionDone is the UNAMBIGUOUS terminal signal the
    // Details loader gates on. voicePrefilled only flips true when >0 answers
    // landed, so a genuinely sparse transcript (0 answers) would otherwise
    // leave the loader running to its full cap. We set surveyExtractionDone in
    // EVERY exit path — success, zero-result, no-case-id, error — so the
    // specialty Details screen reveals the moment work is actually finished,
    // not on a 12s timeout. Reset to false up-front for a fresh run.
    try {
      updateCase({ surveyExtractionDone: false })
    } catch (_) {}
    const markDone = () => { try { updateCase({ surveyExtractionDone: true }) } catch (_) {} }
    try {
      const trimmed = (sourceText || '').trim()
      if (!trimmed) { markDone(); return 0 }
      const specialtyId = currentCase?.specialtyId || currentCase?.specialty_id
      const focusAreaId = currentCase?.focusAreaId || currentCase?.focus_area_id
      if (!specialtyId || !focusAreaId) { markDone(); return 0 }

      const allQuestions = getAllQuestionsForCase(getQuestions, specialtyId, focusAreaId)

      // Profile prefill is deterministic & free — always apply it as the base layer.
      const { answers: profilePrefilled } = prefillFromProfile(profile, allQuestions)

      // ── PRIMARY: Sonnet-backed extraction (only if we have a saved case) ──
      // The endpoint reads the transcript already committed on the case record,
      // so we only attempt it when there is a case id. Returns null on any
      // failure (the api client swallows errors) → we fall through to regex.
      //
      // v219.5.1: set surveyExtractionPending BEFORE the await so the Details
      // screen (DescribeSection) can show a full-screen loader while Sonnet
      // works (~2-4s), instead of revealing an empty questionnaire that fills
      // in late. Cleared in EVERY exit path below (success, empty, error) so
      // the loader can never get permanently stuck. DescribeSection also has
      // an independent 12s safety cap as belt-and-suspenders.
      if (currentCase?.id) {
        updateCase({ surveyExtractionPending: true })
        try {
          const aiResult = await aiAPI.extractSurveyAnswers(
            currentCase.id, allQuestions, language || 'en'
          )
          const aiAnswers = aiResult && aiResult.answers ? aiResult.answers : null
          if (aiAnswers && Object.keys(aiAnswers).length > 0) {
            // AI (high-confidence) wins over profile prefill on conflict.
            const combined = { ...profilePrefilled, ...aiAnswers }
            // v220.11: tag each key with its source so click handlers and
            // future re-runs know what to overwrite vs. preserve. AI wins
            // over profile on the tag too (matches the value-merge above).
            const tags = {}
            for (const k of Object.keys(profilePrefilled)) tags[k] = 'profile'
            for (const k of Object.keys(aiAnswers)) tags[k] = 'voice-ai'
            updateCase({ surveyExtractionPending: false })
            const n = applyPrefills(combined, `${Object.keys(aiAnswers).length} AI + profile`, tags)
            markDone()
            return n
          }
          // AI returned nothing usable (e.g. transcript too sparse) — still
          // apply profile prefill, then fall through to regex as a safety net.
          updateCase({ surveyExtractionPending: false })
        } catch (aiErr) {
          console.warn('[DescribeVoice] AI extraction error, falling back:', aiErr?.message)
          updateCase({ surveyExtractionPending: false })
        }
      }

      // ── FALLBACK: legacy client-side regex matcher ──
      // Reached when: no case id yet, AI unavailable/offline/error, or AI
      // produced zero high-confidence answers. Better than a blank survey.
      const { answers: regexPrefilled } = prefillFromTranscript(trimmed, allQuestions)
      const combined = { ...profilePrefilled, ...regexPrefilled } // transcript wins over profile
      const tags = {}
      for (const k of Object.keys(profilePrefilled)) tags[k] = 'profile'
      for (const k of Object.keys(regexPrefilled)) tags[k] = 'voice-regex'
      const n = applyPrefills(combined, `${Object.keys(regexPrefilled).length} local + profile (fallback)`, tags)
      markDone()
      return n
    } catch (prefillErr) {
      console.log('[DescribeVoice] Prefill skipped:', prefillErr?.message)
      // Safety: never leave the Details loader stuck if something threw.
      try { updateCase({ surveyExtractionPending: false }) } catch (_) {}
      markDone()
      return 0
    }
  }

  const handleNext = async () => {
    // v223.9.4 (hotfix): guard double-taps and show feedback while the save
    // round-trips. Navigation unmounts this screen, so isAdvancing never needs
    // to be reset on the happy path; the try/catch keeps navigation guaranteed
    // even if the save throws (we never want a stuck spinner).
    if (isAdvancing) return
    setIsAdvancing(true)
    try {
      // v206.9.1: Commit the latest text to the case BEFORE running prefill, so the
      // downstream Describe screen reads from the same source of truth. Also covers
      // the case where the user typed/edited and never blurred (mobile users often
      // tap Continue without blurring the textarea first).
      // v219.5.0: AWAIT the save (commits transcript to DB so the AI extractor
      // reads the right text), then fire prefill WITHOUT awaiting so navigation
      // is instant. The questionnaire reads answers reactively from the store —
      // the AI-filled answers land before/while the patient works through it.
      const finalText = (textInput || transcript || '').trim()
      if (finalText) {
        await updateAndSaveCase({
          voiceTextInput: finalText,
          // If user is in text mode or edited the transcript, keep voiceTranscript in sync
          voiceTranscript: finalText,
        })
        // Run prefill against whatever the user committed — typed OR voice.
        // This is the fix: typed input previously bypassed prefill entirely.
        // Non-blocking: do NOT await (AI extraction can take ~2-4s; navigation
        // must stay instant). Errors are swallowed inside runPrefillFromText.
        runPrefillFromText(finalText)
      }
    } catch (e) {
      console.warn('[DescribeVoice] handleNext save failed (navigating anyway):', e?.message)
      setIsAdvancing(false)
    }
    navigate('/case/describe/details')
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const hasContent = voiceState === 'complete' || textInput.trim()

  // ═══════════════════════════════════════════════════════════════════════
  // SUB-COMPONENTS
  // ═══════════════════════════════════════════════════════════════════════
  
  const MetaPill = ({ icon: Icon, label }) => (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium" style={{ backgroundColor: ICE_BLUE, color: BLUE }}>
      <Icon size={10} />{label}
    </span>
  )

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <div className="tabbar-screen-min-h bg-white flex flex-col">
      <ChatKeyframes />
      
      <VoiceRecorderKeyframes />
      
      <CompactHeader
        stepNum={1}
        totalSteps={7}
        stepLabel={`${t('describe.step')} 1 / 6`}
        caseInfo={`${currentCase.specialtyName} › ${currentCase.focusAreaName}`}
        statusLabel={currentCase.status === 'finalized' ? t('caseHome.statusFinalized') : 
          currentCase.status === 'claimed' ? t('caseHome.statusClaimed') :
          currentCase.status === 'shared' ? t('caseHome.statusShared') :
          currentCase.status === 'submitted' ? t('cases.statusSubmitted') : t('caseHome.statusInProgress')}
        onBack={() => caseBack(navigate)}
        onNotification={() => navigate('/notifications')}
      />

      <OrellaChatBubble
        message={t('describe.chatVoice')}
        animationKey={`voice_${currentCase?.id}`}
        onComplete={() => setShowContent(true)}
      />

      {/* Content */}
      <div 
        className="flex-1 px-4 pt-2 pb-4 bg-white"
        style={{ 
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(12px)'
        }}
      >
        {/* v207.1.0 (Smart merge): Mode toggle removed. Unified flow below drives everything off
            `voiceState` + `hasTypedStart`. Idle = hero mic + "or type instead" link. Once any
            content exists (recording or typed), a single editable story card appears. */}

        {/* Locked Banner */}
        {isLocked && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-3">
            <Lock className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-amber-700 font-medium">{t('describe.sectionLocked')}</p>
              <p className="text-amber-600 text-sm">{t('describe.alreadySubmitted')}</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
            <X className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-red-700 text-sm">{error}</p>
              {permissionDenied && <p className="text-red-600 text-xs mt-1">{micPermissionHelp(t, isNativeApp())}</p>}
            </div>
          </div>
        )}

        {/* ═══════════ UNIFIED FLOW (v207.1.0 Smart merge) ═══════════ */}
        <div className="mb-4">

          {/* IDLE HERO — mic-first, "or type instead" secondary link */}
          {voiceState === 'idle' && !hasTypedStart && !textInput && (
            <RecorderIdleHero
              ns="describeVoice"
              locked={isLocked}
              lockedLabel={t('describe.recordingLocked')}
              onRecord={handleStartRecording}
              onType={() => {
                setHasTypedStart(true)
                setTimeout(() => textareaRef.current?.focus(), 80)
              }}
            />
          )}

          {/* RECORDING / PAUSED — unchanged behavior */}
          {(voiceState === 'recording' || voiceState === 'paused') && (
            <RecorderLivePanel
              ns="describeVoice"
              state={voiceState}
              seconds={recordingTime}
              level={level}
              heardSpeech={heardSpeech}
              onPause={handlePauseRecording}
              onResume={handleResumeRecording}
              onStop={handleStopRecording}
            />
          )}

          {/* TRANSCRIBING */}
          {voiceState === 'tidying' && <TidyingPanel ns="describeVoice" />}

          {/* The 'transcribing' progress panel was removed with Whisper
              (WHISPER-REMOVAL-1). It showed a staged progress bar while the
              batch leg ran; nothing sets that state any more, because there is
              no round trip to narrate. Streaming produces text live. */}

          {/* UNIFIED STORY CARD — shown whenever content exists (voice OR typed).
              No Voice/Text toggle, no separate Edit button. Always editable. */}
          {(voiceState === 'complete' || hasTypedStart || (!!textInput && voiceState === 'idle')) && voiceState !== 'recording' && voiceState !== 'paused' && voiceState !== 'transcribing' && (
            <div className="space-y-3">

              {/* Playback bar — only if audio exists */}
              {voiceState === 'complete' && audioUrlRef.current && (
                <div className="bg-gray-50 rounded-xl p-3.5 flex items-center gap-3 border border-gray-100">
                  <button onClick={handlePlayPause}
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${BLUE}, #3A8FD6)`, boxShadow: `0 2px 8px ${BLUE}30` }}>
                    {isPlaying ? <Pause size={14} className="text-white" /> : <Play size={14} className="text-white ml-0.5" />}
                  </button>
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-[#46A8F9] transition-all"
                      style={{ width: `${recordingTime > 0 ? (playbackTime / recordingTime) * 100 : 0}%` }} />
                  </div>
                  <span className="text-xs font-medium text-gray-400 tabular-nums w-10 text-right">
                    {isPlaying ? formatTime(playbackTime) : formatTime(recordingTime)}
                  </span>
                </div>
              )}

              {/* Meta pills — only for voice */}
              {voiceState === 'complete' && (
                <div className="flex gap-1.5 flex-wrap">
                  {detectedLang && <MetaPill icon={Globe} label={detectedLang} />}
                  {confidence && <MetaPill icon={ShieldCheck} label={`${confidence}% confidence`} />}
                  {/* v242.57.0 (D-TIDY): disclosure that the text was cleaned up.
                      Green reads as confirmation here, not as the Deliver phase. */}
                  {wasTidied && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium"
                      style={{ backgroundColor: '#F0FBF3', color: '#2E7D4F' }}
                    >
                      <Check size={10} />{t('describeVoice.fillerWordsRemoved')}
                    </span>
                  )}
                  {currentCase?.voiceRecording?.aiEnhanced && (
                    <MetaPill icon={Sparkles} label={t('describeVoice.aiEnhanced')} />
                  )}
                </div>
              )}

              {/* Unified editable story card — YOUR STORY */}
              <div className="rounded-xl border-[1.5px] border-[#46A8F9] bg-blue-50/30 overflow-hidden">
                <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-blue-100/60 bg-white/60">
                  <span className="text-[11px] font-bold text-[#15354F] uppercase tracking-wide">
                    {t('describeVoice.yourStory') || 'Your story'}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {(textInput || transcript).length} {t('describe.characters')}
                  </span>
                </div>
                <textarea
                  ref={textareaRef}
                  value={textInput || transcript}
                  onChange={(e) => {
                    if (isLocked) return
                    setTextInput(e.target.value)
                    setTranscript(e.target.value)
                    // v242.57.0: once the patient types, "filler words removed"
                    // describes text that no longer exists. Retract the claim.
                    if (wasTidied) { setWasTidied(false); setShowOriginal(false) }
                  }}
                  onBlur={() => {
                    const val = (textInput || transcript).trim()
                    if (val) updateAndSaveCase({ voiceTextInput: val, voiceTranscript: val })
                  }}
                  onInput={(e) => {
                    // v213.3 (C-A-186): auto-grow so the textarea never has its own
                    // inner scroll — fixes a bug where touch gestures inside the
                    // textarea could not scroll the page past it.
                    // v217.1.1: 600px cap removed (see useEffect comment).
                    const ta = e.currentTarget
                    ta.style.height = 'auto'
                    ta.style.height = ta.scrollHeight + 'px'
                  }}
                  disabled={isLocked}
                  placeholder={t('describe.typeYourStory')}
                  className={`w-full min-h-[130px] p-3.5 text-[13px] leading-relaxed bg-transparent border-none outline-none resize-none overflow-hidden ${
                    isLocked ? 'text-gray-500 cursor-not-allowed' : 'text-[#15354F]'
                  }`}
                />

                {/* v242.57.0 (D-TIDY) — one tap to the verbatim transcript.
                    Only rendered while the tidied claim is still true. */}
                {wasTidied && !isLocked && rawTranscript && (
                  <div className="px-3.5 py-2.5 border-t border-blue-100/60 bg-white/50">
                    <button
                      onClick={() => setShowOriginal(v => !v)}
                      className="inline-flex items-center gap-1.5 text-[11.5px] font-bold"
                      style={{ color: BLUE }}
                    >
                      <Eye size={12} />
                      {showOriginal
                        ? t('describeVoice.hideOriginal')
                        : t('describeVoice.seeOriginal')}
                    </button>
                  </div>
                )}
              </div>

              {wasTidied && showOriginal && !isLocked && rawTranscript && (
                <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#E8EDF2', background: '#FBFCFD' }}>
                  <div className="flex items-center justify-between px-3.5 py-2.5 border-b bg-white" style={{ borderColor: '#E8EDF2' }}>
                    <span className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: '#64748B' }}>
                      {t('describeVoice.whatYouSaid')}
                    </span>
                    <button
                      onClick={() => setShowOriginal(false)}
                      className="text-[11.5px] font-semibold"
                      style={{ color: '#64748B' }}
                    >
                      {t('common.close') || 'Close'}
                    </button>
                  </div>
                  <div className="px-3.5 py-3 text-[13px] leading-relaxed" style={{ color: '#5B6B7C' }}>
                    {rawTranscript}
                  </div>
                  <div className="px-3.5 py-2.5 border-t bg-white" style={{ borderColor: '#E8EDF2' }}>
                    <button
                      onClick={handleUseOriginal}
                      className="w-full py-2.5 rounded-full text-[12px] font-bold text-white"
                      style={{ backgroundColor: NAVY }}
                    >
                      {t('describeVoice.useOriginalWords')}
                    </button>
                  </div>
                </div>
              )}

              {/* Secondary actions — "Record instead" (typed) or "Re-record" (voice).
                  Both wipe current content. Inline warning copy only — no confirm dialog. */}
              {!isLocked && (
                <div className="flex items-center justify-center">
                  <button
                    onClick={handleDeleteRecording}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-[#15354F] transition-colors px-3 py-1.5 rounded-full border border-gray-200 bg-white"
                  >
                    {voiceState === 'complete' ? (
                      <>
                        <RotateCcw size={12} /> {t('describeVoice.reRecordClears') || 'Re-record (clears this)'}
                      </>
                    ) : (
                      <>
                        <Mic size={12} /> {t('describeVoice.recordInsteadClears') || 'Record instead (clears text)'}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Buttons — PROD-1194 part C: pinned on top of the TabBar (BottomActionBar aboveTabBar),
          so Next stays in sight while the story card scrolls; the fade-in rides on the bar itself. */}
      <BottomActionBar
        aboveTabBar
        className="[--bar-px:16px]"
        style={{ 
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          transitionDelay: '150ms',
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(8px)'
        }}
      >
        <div className="flex items-center gap-2.5">
          {!isLocked && (
            <button onClick={async () => {
              // v206.9.1: Skip to End must also commit text and run prefill,
              // otherwise typed input bypasses survey auto-population.
              // v219.5.0: await the save so the transcript is committed before
              // AI extraction reads it; fire prefill non-blocking, then navigate.
              const finalText = (textInput || transcript || '').trim()
              if (finalText) {
                await updateAndSaveCase({ voiceTextInput: finalText, voiceTranscript: finalText })
                runPrefillFromText(finalText)
              }
              // v213.3 (C-A-189): Skip jumps straight to the View Summary page
              // (where the patient submits) instead of the intermediate Review.
              navigate('/case/describe/summary')
            }}
              className="px-5 py-3 rounded-full border-[1.5px] border-gray-200 text-gray-400 text-[14px] font-semibold flex-shrink-0 active:bg-gray-50 transition-colors whitespace-nowrap">
              {t('common.skipToEnd') || 'Skip to End'}
            </button>
          )}
          <button onClick={handleNext}
            disabled={isAdvancing}
            className={`flex-1 py-3 rounded-full font-bold text-[15px] transition-colors flex items-center justify-center gap-2 ${
              hasContent ? 'bg-[#46A8F9] text-white active:bg-[#3A8FD6]' : 'bg-[#46A8F9]/60 text-white/80 active:bg-[#46A8F9]/70'
            } ${isAdvancing ? 'cursor-wait opacity-90' : (hasContent ? 'breathe-blue' : '')}`}>
            {isAdvancing ? (
              <>
                <OrellaSpinner size={16} tone="inherit" />
                {t('common.preparingNextSteps') || 'Preparing next steps'}
              </>
            ) : (
              t('common.next') || 'Next'
            )}
          </button>
          {!isLocked && (
            <button onClick={() => setShowDeleteModal(true)}
              className="w-[42px] h-[42px] rounded-full border-[1.5px] border-red-100 flex items-center justify-center flex-shrink-0 active:bg-red-50 transition-colors">
              <Trash2 size={16} className="text-red-400" />
            </button>
          )}
        </div>
      </BottomActionBar>
      
      <DeleteCaseModal canDelete={!isHeldByDoctor(currentCase)}
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeleteCase}
        onSaveAsDraft={handleSaveAsDraft}
        caseName={currentCase?.focusAreaName || currentCase?.focusArea?.name || ''}
      />
    </div>
  )
}
